import React, { useEffect, useState } from 'react';
import { C } from '../../theme';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { Trash2 } from 'lucide-react';

const BLANK = { name: '', base_cpm: 0 };

export default function TrackersPage() {
  const { canEdit } = useAuth();
  const [trackers, setTrackers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [eligibility, setEligibility] = useState([]); // [{platform_id, tracker_id, eligible}]
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | tracker object
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  function load() {
    Promise.all([
      api.getTrackers(false),
      api.getPlatforms(true),
      api.getTrackerEligibility(),
    ]).then(([trs, plats, elig]) => {
      setTrackers(trs);
      setPlatforms(plats);
      setEligibility(elig);
    }).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'new') await api.createTracker({ name: form.name, base_cpm: parseFloat(form.base_cpm) || 0 });
      else await api.updateTracker(modal.id, { name: form.name, base_cpm: parseFloat(form.base_cpm) || 0 });
      setModal(null); load();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  }

  async function handleDelete(t) {
    if (!window.confirm(`Delete tracker "${t.name}"?`)) return;
    await api.deleteTracker(t.id); load();
  }

  function isEligible(platformId, trackerId) {
    const row = eligibility.find(e => e.platform_id === platformId && e.tracker_id === trackerId);
    return !!row?.eligible;
  }

  async function toggleEligible(platformId, trackerId) {
    const next = !isEligible(platformId, trackerId);
    setEligibility(prev => {
      const exists = prev.some(e => e.platform_id === platformId && e.tracker_id === trackerId);
      return exists
        ? prev.map(e => (e.platform_id === platformId && e.tracker_id === trackerId) ? { ...e, eligible: next } : e)
        : [...prev, { platform_id: platformId, tracker_id: trackerId, eligible: next }];
    });
    try { await api.setTrackerEligibility({ platform_id: platformId, tracker_id: trackerId, eligible: next }); }
    catch (e) { load(); } // resync on failure
  }

  if (loading) return <div style={{ ...C.page, color: '#8aa0b8' }}>Loading…</div>;

  return (
    <div style={C.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <h1 style={C.title}>Trackers</h1>
        {canEdit && <button style={C.btnPrimary} onClick={() => { setForm(BLANK); setModal('new'); }}>+ New Tracker</button>}
      </div>

      <table style={{ ...C.table, marginBottom: 36 }}>
        <thead>
          <tr>
            <th style={C.th}>Name</th>
            <th style={{ ...C.th, textAlign: 'right' }}>Base CPM €</th>
            <th style={C.th}>Status</th>
            <th style={C.th}></th>
          </tr>
        </thead>
        <tbody>
          {trackers.length === 0 ? (
            <tr><td colSpan={4} style={{ ...C.td, textAlign: 'center', color: '#8aa0b8', padding: 48 }}>No trackers yet.</td></tr>
          ) : trackers.map(t => (
            <tr key={t.id} style={C.tr}>
              <td style={{ ...C.td, fontWeight: 700 }}>{t.name}</td>
              <td style={{ ...C.td, textAlign: 'right' }}>€{Number(t.base_cpm || 0).toFixed(2)}</td>
              <td style={C.td}>
                <span style={C.statusRow()}>
                  <span style={C.statusDot(t.active ? 'success' : 'neutral')} />
                  {t.active ? 'active' : 'inactive'}
                </span>
              </td>
              <td style={{ ...C.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {canEdit && <>
                  <button style={C.btnSecondary} onClick={() => { setForm({ name: t.name, base_cpm: t.base_cpm }); setModal(t); }}>Edit</button>
                  <button style={C.btnDanger} onClick={() => handleDelete(t)}><Trash2 size={13} /></button>
                </>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a2a3a', marginBottom: 6 }}>Platform × Tracker eligibility</h2>
      <p style={{ color: '#8aa0b8', fontSize: 13, marginBottom: 16 }}>
        A tracker only applies to a platform when checked here. Unchecked (or never set) means not eligible.
      </p>

      {trackers.length === 0 || platforms.length === 0 ? (
        <div style={C.empty}>Add at least one tracker and one platform to configure eligibility.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={C.table}>
            <thead>
              <tr>
                <th style={C.th}>Platform</th>
                {trackers.map(t => (
                  <th key={t.id} style={{ ...C.th, textAlign: 'center' }}>{t.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {platforms.map(p => (
                <tr key={p.id} style={C.tr}>
                  <td style={C.td}>{p.name}</td>
                  {trackers.map(t => (
                    <td key={t.id} style={{ ...C.td, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isEligible(p.id, t.id)}
                        disabled={!canEdit}
                        onChange={() => toggleEligible(p.id, t.id)}
                        style={{ width: 16, height: 16, cursor: canEdit ? 'pointer' : 'default' }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(80,120,160,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setModal(null)}>
          <div style={C.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 22px', fontSize: 16, fontWeight: 800, color: '#1a2a3a' }}>{modal === 'new' ? 'New Tracker' : `Edit: ${modal.name}`}</h2>
            <label style={{ display: 'block', marginBottom: 14 }}>
              <span style={C.label}>Name *</span>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Gemius / GDE" style={C.input} autoFocus />
            </label>
            <label style={{ display: 'block', marginBottom: 22 }}>
              <span style={C.label}>Base CPM €</span>
              <input type="number" step="0.01" defaultValue={form.base_cpm} onBlur={e => setForm(p => ({ ...p, base_cpm: e.target.value }))} style={C.input} />
            </label>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={C.btnSecondary} onClick={() => setModal(null)}>Cancel</button>
              <button style={C.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
